// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react'
import { Phone, PhoneIncoming, PhoneOutgoing, AlertTriangle, Package, MessageCircle, Music, ArrowUpDown, ArrowUp, ArrowDown, ChevronDown, ChevronUp, Play, Pause, X, PhoneCall } from 'lucide-react'
import { useCallFormatters } from '../hooks/useCallFormatters'
import { formatPhoneNumberForDisplay, isAnonymousNumber } from '../utils/phoneNumberHelper'

/**
 * 通話履歴テーブルコンポーネント
 *
 * @param {object} props
 * @param {array} props.callHistory - 通話履歴配列
 * @param {function} props.onEditMemo - 詳細ボタン押下時のコールバック
 * @param {function} props.onCallback - 折り返しボタン押下時のコールバック
 * @param {function} props.onSortChange - ソート変更時のコールバック
 * @param {string} props.sortBy - 現在のソート対象フィールド
 * @param {string} props.sortOrder - ソート順序 ('asc' or 'desc')
 * @param {string} props.displayMode - 表示モード ('full', 'compact', 'accordion')
 * @param {object} props.columns - 表示する列の設定
 * @param {boolean} props.compactRowHeight - 行高を縮小するか
 * @param {string} props.emptyMessage - データがない時のメッセージ
 * @param {object} props.phoneNumbers - 電話番号の名前マップ（キー: 電話番号、値: { name, phoneNumber }）
 */
export default function CallHistoryTable({
  callHistory,
  onEditMemo,
  onCallback,
  onSortChange,
  sortBy,
  sortOrder,
  displayMode = 'full',
  columns = {},
  compactRowHeight = false,
  emptyMessage = '通話履歴がありません',
  phoneNumbers = {},
  onPlaybackChange = null
}) {
  const [expandedRows, setExpandedRows] = useState({})
  const [playingCallId, setPlayingCallId] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const audioRef = useRef(null)
  const prevDirectionsRef = useRef({})

  // DEBUG: originalDirectionの変化を検知
  useEffect(() => {
    const prev = prevDirectionsRef.current
    callHistory.slice(0, 5).forEach((call) => {
      const id = call.callHistoryId
      if (prev[id] && prev[id] !== call.originalDirection) {
        console.error('[CallHistoryTable] originalDirection変化検知!',
          id?.slice(-8), prev[id], '→', call.originalDirection)
        console.trace('[CallHistoryTable] 変化のスタックトレース')
      }
      prev[id] = call.originalDirection
    })
    prevDirectionsRef.current = prev
  }, [callHistory])

  const {
    formatDuration,
    formatDateTime,
    formatPhoneNumber,
    getCallTypeLabel,
    getCallTypeIcon,
    renderStatusBadge
  } = useCallFormatters()

  // デフォルトの列設定
  // 列順: 通話相手 → 利用番号 → 担当 → 通話時間 → ステータス → 日時 → 操作
  const defaultColumns = {
    customer: true,
    toNumber: displayMode === 'full', // 利用番号（発着信に使用した電話番号）
    operator: displayMode === 'full',
    duration: true,
    status: true,
    dateTime: true,
    actions: true
  }

  // 電話番号の表示名を取得
  const getPhoneNumberDisplay = (phoneNumber) => {
    const phoneInfo = phoneNumbers[phoneNumber]
    const formatted = formatPhoneNumber(phoneNumber) || '不明'
    return {
      primary: phoneInfo?.name || formatted,
      secondary: phoneInfo?.name ? formatted : '',
    }
  }

  const visibleColumns = { ...defaultColumns, ...columns }

  // ソートアイコン
  const getSortIcon = (field) => {
    if (!onSortChange) return null
    if (sortBy !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />
    }
    return sortOrder === 'asc' ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />
  }

  // 行の展開/折りたたみ
  const toggleRowExpand = (callHistoryId) => {
    setExpandedRows(prev => ({
      ...prev,
      [callHistoryId]: !prev[callHistoryId]
    }))
  }

  // 再生状態の変化を親に通知
  useEffect(() => {
    if (onPlaybackChange) {
      onPlaybackChange(isPlaying || playingCallId !== null)
    }
  }, [isPlaying, playingCallId, onPlaybackChange])

  // 録音再生関連
  const handlePlayRecording = (call) => {
    if (playingCallId === call.callHistoryId) {
      // 同じ録音をトグル
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause()
        } else {
          audioRef.current.play()
        }
      }
    } else {
      // 新しい録音を再生
      setPlayingCallId(call.callHistoryId)
      setIsPlaying(false)
    }
  }

  const handleClosePlayer = () => {
    if (audioRef.current) {
      audioRef.current.pause()
    }
    setPlayingCallId(null)
    setIsPlaying(false)
  }

  // アコーディオンモードのレンダリング
  if (displayMode === 'accordion') {
    return (
      <div className="space-y-2">
        {callHistory.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm">
            {emptyMessage}
          </div>
        ) : (
          callHistory.map((call) => (
            <div
              key={call.callHistoryId}
              className="border border-gray-200 rounded-md overflow-hidden"
            >
              {/* アコーディオンヘッダー */}
              <button
                onClick={() => toggleRowExpand(call.callHistoryId)}
                className="w-full px-3 py-2 bg-white hover:bg-gray-50 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-600">{formatDateTime(call.startTime)}</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-700">{formatDuration(call.duration)}</span>
                  {call.callType && (
                    <>
                      <span className="text-gray-400">|</span>
                      <span className="flex items-center gap-1">
                        {getCallTypeIcon(call.callType)}
                        <span className="text-xs">{getCallTypeLabel(call.callType)}</span>
                      </span>
                    </>
                  )}
                </div>
                {expandedRows[call.callHistoryId] ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </button>

              {/* アコーディオン詳細 */}
              {expandedRows[call.callHistoryId] && (
                <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-sm">
                  <div className="space-y-1">
                    {call.handledByName && (
                      <div className="text-gray-600">
                        担当: <span className="text-gray-900">{call.handledByName}</span>
                      </div>
                    )}
                    {(call.summary || call.notes) && (
                      <div className="text-gray-700 mt-2">
                        {call.summary || call.notes}
                      </div>
                    )}
                    {call.tags && call.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {call.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {onEditMemo && (
                      <div className="mt-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onEditMemo(call)
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          詳細を見る
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    )
  }

  // コンパクトモードのレンダリング
  if (displayMode === 'compact') {
    return (
      <div className="space-y-2">
        {callHistory.length === 0 ? (
          <div className="text-center py-4 text-gray-500 text-sm">
            {emptyMessage}
          </div>
        ) : (
          callHistory.map((call) => (
            <div
              key={call.callHistoryId}
              className="p-3 border border-gray-200 rounded-md bg-white hover:bg-gray-50"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 text-sm">
                  <span className="text-gray-600">{formatDateTime(call.startTime)}</span>
                  <span className="text-gray-700">{formatDuration(call.duration)}</span>
                  {renderStatusBadge(call.status)}
                </div>
                {onEditMemo && (
                  <button
                    onClick={() => onEditMemo(call)}
                    className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                  >
                    詳細
                  </button>
                )}
              </div>
              {(call.summary || call.notes) && (
                <div className="mt-1 text-sm text-gray-600 truncate">
                  {call.summary || call.notes}
                </div>
              )}
              {call.tags && call.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {call.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    )
  }

  // フルモード（テーブル）のレンダリング
  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {/* 通話相手 */}
              {visibleColumns.customer && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  通話相手
                </th>
              )}
              {/* 利用番号 */}
              {visibleColumns.toNumber && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  利用番号
                </th>
              )}
              {/* 担当 */}
              {visibleColumns.operator && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  担当
                </th>
              )}
              {/* 通話時間 */}
              {visibleColumns.duration && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  通話時間
                </th>
              )}
              {/* ステータス */}
              {visibleColumns.status && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ステータス
                </th>
              )}
              {/* 日時 */}
              {visibleColumns.dateTime && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  日時
                </th>
              )}
              {/* 操作 */}
              {visibleColumns.actions && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {callHistory.length === 0 ? (
              <tr>
                <td
                  colSpan={Object.values(visibleColumns).filter(Boolean).length}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              callHistory.map((call, idx) => {
                // DEBUG: 最初の3件のoriginalDirectionを毎レンダリングでログ
                if (idx < 3) {
                  console.log('[CallHistoryTable] render idx=', idx,
                    'id=', call.callHistoryId?.slice(-8),
                    'originalDirection=', call.originalDirection,
                    'direction=', call.direction,
                    'status=', call.status)
                }
                return (
                <React.Fragment key={call.callHistoryId}>
                <tr
                  onClick={() => onEditMemo && onEditMemo(call)}
                  className={`hover:bg-gray-50 cursor-pointer ${playingCallId === call.callHistoryId ? 'bg-green-50' : ''}`}
                >
                  {/* 通話相手 */}
                  {visibleColumns.customer && (
                    <td className={`px-4 ${compactRowHeight ? 'py-2' : 'py-5'} text-sm text-gray-900`}>
                      <div className="flex items-center gap-3">
                        {/* 着信/発信アイコン（originalDirectionのみ使用） */}
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          call.originalDirection === 'inbound'
                            ? 'bg-blue-100 dark:bg-blue-900/30'
                            : 'bg-green-100 dark:bg-green-900/30'
                        }`}>
                          {call.originalDirection === 'inbound' ? (
                            <PhoneIncoming className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          ) : (
                            <PhoneOutgoing className="w-4 h-4 text-green-600 dark:text-green-400" />
                          )}
                        </div>
                        <div>
                          {(() => {
                            // 通話相手の電話番号を取得（着信時はfrom、発信時はto）
                            const customerPhone = call.originalDirection === 'inbound'
                              ? (call.from || call.phoneNumber)
                              : call.to
                            // customerNameが電話番号形式（+または数字のみ）またはAnonymousの場合は未登録扱い
                            const isPhoneNumberName = call.customerName && /^[\d+\-\s()]+$/.test(call.customerName)
                            const isAnonymous = call.customerName === 'Anonymous' || call.customerName === 'anonymous'
                            const hasValidName = call.customerName && !isPhoneNumberName && !isAnonymous

                            return hasValidName ? (
                              <>
                                <div className="font-bold text-gray-900">{call.customerName}</div>
                                <div className="text-xs font-bold text-gray-600">{formatPhoneNumber(customerPhone)}</div>
                              </>
                            ) : (
                              <>
                                <div className="font-bold text-gray-500">未登録</div>
                                <div className="text-xs font-bold text-gray-600">{formatPhoneNumber(customerPhone)}</div>
                              </>
                            )
                          })()}
                        </div>
                      </div>
                    </td>
                  )}
                  {/* 利用番号 */}
                  {visibleColumns.toNumber && (
                    <td className={`px-4 ${compactRowHeight ? 'py-2' : 'py-5'} text-sm text-gray-900`}>
                      {(() => {
                        // 利用した自社の電話番号（着信時はto、発信時はfrom）
                        const usedNumber = call.originalDirection === 'inbound' ? call.to : call.from
                        const toDisplay = getPhoneNumberDisplay(usedNumber)
                        return (
                          <div>
                            <div className="font-medium">{toDisplay.primary}</div>
                            {toDisplay.secondary && (
                              <div className="text-xs text-gray-500">{toDisplay.secondary}</div>
                            )}
                          </div>
                        )
                      })()}
                    </td>
                  )}
                  {/* 担当 */}
                  {visibleColumns.operator && (
                    <td className={`px-4 ${compactRowHeight ? 'py-2' : 'py-5'} whitespace-nowrap text-sm text-gray-700`}>
                      {call.handledByName || '未対応'}
                    </td>
                  )}
                  {/* 通話時間 */}
                  {visibleColumns.duration && (
                    <td className={`px-4 ${compactRowHeight ? 'py-2' : 'py-5'} whitespace-nowrap text-sm text-gray-700`}>
                      {formatDuration(call.duration)}
                    </td>
                  )}
                  {/* ステータス */}
                  {visibleColumns.status && (
                    <td className={`px-4 ${compactRowHeight ? 'py-2' : 'py-5'} whitespace-nowrap`}>
                      {renderStatusBadge(call.status)}
                    </td>
                  )}
                  {/* 日時 */}
                  {visibleColumns.dateTime && (
                    <td className={`px-4 ${compactRowHeight ? 'py-2' : 'py-5'} whitespace-nowrap text-sm text-gray-900`}>
                      {formatDateTime(call.startTime)}
                    </td>
                  )}
                  {visibleColumns.actions && (
                    <td className={`px-4 ${compactRowHeight ? 'py-2' : 'py-5'} whitespace-nowrap text-sm`}>
                      <div className="flex items-center gap-2">
                        {/* 折り返しボタン（着信のみ表示、非通知はdisabled） */}
                        {onCallback && call.originalDirection === 'inbound' && (
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              if (!isAnonymousNumber(call.from) && !isAnonymousNumber(call.phoneNumber)) {
                                onCallback(call)
                              }
                            }}
                            disabled={isAnonymousNumber(call.from) || isAnonymousNumber(call.phoneNumber)}
                            className={`p-3 rounded-full transition-colors ${
                              isAnonymousNumber(call.from) || isAnonymousNumber(call.phoneNumber)
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                            }`}
                            title={isAnonymousNumber(call.from) || isAnonymousNumber(call.phoneNumber) ? '非通知のため折り返しできません' : '折り返し発信'}
                          >
                            <PhoneCall className="w-5 h-5" />
                          </button>
                        )}
                        {/* 録音再生ボタン */}
                        {call.recordingUrl && (
                          <button
                            onClick={(e) => {
                              e.preventDefault()
                              e.stopPropagation()
                              handlePlayRecording(call)
                            }}
                            className={`p-3 rounded-full transition-colors ${
                              playingCallId === call.callHistoryId
                                ? 'bg-green-100 text-green-700'
                                : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                            }`}
                            title="録音を再生"
                          >
                            {playingCallId === call.callHistoryId && isPlaying ? (
                              <Pause className="w-5 h-5" />
                            ) : (
                              <Play className="w-5 h-5" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>

                {/* インライン録音プレーヤー（この行の下に表示） */}
                {playingCallId === call.callHistoryId && call.recordingUrl && (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="p-0">
                      <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3">
                        <div className="flex items-center gap-4">
                          {/* 通話情報 */}
                          <div className="flex-shrink-0 min-w-0">
                            <div className="text-sm font-medium text-gray-900">
                              {(() => {
                                const phoneNumber = call.phoneNumber || call.from || call.to
                                const customerName = call.customerName
                                
                                // customerNameが存在する場合
                                if (customerName) {
                                  // customerNameをformatPhoneNumberForDisplayで処理（非通知判定含む）
                                  const formattedCustomerName = formatPhoneNumberForDisplay(customerName)
                                  
                                  // 「非通知」と判定された場合はそれを返す
                                  if (formattedCustomerName === '非通知') {
                                    return '非通知'
                                  }
                                  
                                  // customerNameが電話番号形式でない場合はそのまま表示
                                  if (!/^\+?\d{10,}$/.test(customerName.replace(/[\s\-()]/g, ''))) {
                                    return customerName
                                  }
                                }
                                
                                // それ以外はphoneNumberをフォーマット
                                return formatPhoneNumberForDisplay(phoneNumber)
                              })()}
                            </div>
                            <div className="text-xs text-gray-500">
                              {formatDateTime(call.startTime)}
                            </div>
                          </div>

                          {/* ネイティブオーディオプレーヤー */}
                          <div className="flex-1 min-w-0">
                            <audio
                              ref={audioRef}
                              src={call.recordingUrl}
                              controls
                              controlsList="nodownload"
                              preload="metadata"
                              className="w-full h-10"
                              onPlay={() => setIsPlaying(true)}
                              onPause={() => setIsPlaying(false)}
                              onEnded={() => setIsPlaying(false)}
                              onCanPlay={() => {
                                // 自動再生
                                if (audioRef.current && !isPlaying) {
                                  audioRef.current.play().catch(() => {
                                    // 自動再生がブロックされた場合は無視
                                  })
                                }
                              }}
                            />
                          </div>

                          {/* 閉じるボタン */}
                          <button
                            onClick={handleClosePlayer}
                            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-white/50 rounded transition-colors flex-shrink-0"
                            title="閉じる"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              )})
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
